import { sortPrompts } from '@/util/sort';
import SearchField from '@/components/common/SearchField';
import { useApplication } from '@/hooks/useApplication';
import { Prompt, PromptSortType } from '@/types/prompt';
import { SearchBlocksItem } from '@/types/search';
import { filterPrompts, promptSearchOptions } from '@/util/prompts';
import { isString } from '@/util/typeGuards';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';
import PromptManager from './PromptManager';
import { useLocalStorage } from 'usehooks-ts';

type PromptManagerProps = {
  open: boolean;
  promptGroupId: number;
  onPromptSearch: (searchValue: SearchBlocksItem) => void;
  onClose: () => void;
};

const PromptDialog: FC<PromptManagerProps> = (props) => {
  const { application } = useApplication();

  const [prompts, setPrompts] = useState<Prompt[]>();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useLocalStorage<PromptSortType>('promptSort', 'label');
  const [invert, setInvert] = useState<boolean>(false);

  const promptGroup = application?.promptGroups?.find((promptGroup) => promptGroup.id === props.promptGroupId);

  const filteredPrompts = filterPrompts(prompts, searchQuery);

  const handleClose = () => {
    setPrompts([]);
    props.onClose();
  };

  useEffect(() => {
    if (!props.open) return;

    const promptGroupPrompts = props.promptGroupId
      ? application.prompts.filter((prompts) => prompts.promptGroup === props.promptGroupId)
      : application.prompts;
    setPrompts(promptGroupPrompts);
  }, [props.open, props.promptGroupId, application.prompts]);

  return (
    <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>
        <Box>{promptGroup ? 'Prompt Group - ' + promptGroup.name : 'Prompts - All'}</Box>
        {promptGroup && (
          <Box>
            {promptGroup.path && (
              <code>{isString(promptGroup.path) ? promptGroup.path : `[${promptGroup.path?.name}]`}</code>
            )}
          </Box>
        )}
        {application?.prompts.length > 2 && (
          <SearchField
            searchOptions={promptSearchOptions}
            searchQuery={searchQuery}
            setSearchQuery={(query) => setSearchQuery(query)}
            sortBy={sortBy}
            invert={invert}
            onSort={(changeSortBy, reverse) => {
              changeSortBy && setSortBy(changeSortBy as PromptSortType);
              reverse && setInvert(!invert);
            }}
            autoFocus
          />
        )}
      </DialogTitle>
      <DialogContent>
        <PromptManager
          promptGroupId={props.promptGroupId}
          filteredPrompts={sortPrompts(filteredPrompts, sortBy, invert, 'file')}
          onPromptSearch={(searchValue) => {
            props.onPromptSearch(searchValue);
            handleClose();
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color='primary'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromptDialog;
